import cn from 'classnames'
import * as React from 'react'
import { Icon } from '../icon'
import { Typography } from '../typography'
import styles from './Token.module.css'
import { TokenProps } from './Token.types'

function Token({
  children,
  value,
  dismissible,
  onDismissClick,
  disabled,
  onClick,
  icon,
  rightIcon,
  type = 'button',
  className,
  ...rest
}: React.PropsWithChildren<TokenProps>) {
  const [visibleDisabled, setVisibleDisabled] = React.useState(false)

  // Defer applying the disabled class by a tick so the opacity transition runs
  // when tokens fade together (e.g. during a slow search response).
  React.useEffect(() => {
    if (disabled) {
      const t = setTimeout(() => setVisibleDisabled(true), 0)
      return () => clearTimeout(t)
    }
    else {
      setVisibleDisabled(false)
    }
  }, [disabled])

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!disabled && dismissible && value && onDismissClick) {
        onDismissClick(value, e)
      }

      if (onClick) {
        onClick(e)
      }
    },
    [disabled, dismissible, value, onDismissClick, onClick],
  )

  return (
    <button
      type={type}
      className={cn(styles.token, visibleDisabled && styles.disabled, className)}
      {...rest}
      tabIndex={!disabled ? 0 : undefined}
      onClick={handleClick}
    >
      {icon && <Icon glyph={icon} size={20} className={styles.leftIcon} />}
      <Typography
        variant="body-m-regular-tight"
        className={cn(icon && styles.textWithIcon)}
      >
        {children}
      </Typography>
      {rightIcon && <Icon glyph={rightIcon} size={20} className={styles.rightIcon} />}
      {dismissible && value && <Icon glyph="DismissSmall" size={20} className={styles.rightIcon} />}
    </button>
  )
}

Token.displayName = 'Token'

export default Token
